import { useI18n } from 'vue-i18n';
import { camelCase } from 'change-case';
import { constants } from '~/constants';
import { toPascalCaseName } from '~/utils/helper';
import type { DisplayExprType } from '~/types';

export const useUtils = () => {
  const { t } = useI18n();
  const transactionsStatusText = 'transactions.status';
  const runtimeConfig = useConfig();
  const notProduction = computed(
    () => runtimeConfig.value.public.lemonwayEnv.toLowerCase() !== 'production'
  );

  const displayCallbacks: { [key: string]: Function } = {
    holderAuthenticationTypes: (element: DisplayExprType) =>
      t(`transactions.secureDetails.HolderAuthenticationTypes.${element.text}`),
    challengeModes: (element: DisplayExprType) =>
      t(`transactions.secureDetails.challengeModes.${element.text}`),
    transactionBankingStatuses: (element: DisplayExprType) =>
      t(`transactions.bankingStatus.${element.text}`),
    transactionStatuses: (value: string) =>
      t(`${transactionsStatusText}.${constants.transactionStatuses[value].text}`),
    transactionTypes: (element: DisplayExprType) => t(`transactions.type.${element.text}`),
    transactionCategories: (element: DisplayExprType) => t(`transactions.category.${element.text}`),
    paymentMethods: (element: DisplayExprType) => t(`transactions.method.${element.text}`),
    paymentMeanDataProviders: (value: string) => constants.paymentMeanDataProviders[value],
    bankTransferTypes: (element: DisplayExprType) =>
      t(`transactions.bankTransferType.${element.text}`),
    bankAccountTypes: (element: DisplayExprType) => t(`wallets.bankAccounts.${element.text}`),
    transactionP2PStatuses: (value: string) =>
      t(`${transactionsStatusText}.${constants.transactionP2PStatuses[value].text}`),
    transactionInOutStatuses: (value: string) =>
      t(`${transactionsStatusText}.${constants.transactionInOutStatuses[value].text}`),
    bankAccountStatuses: (element: DisplayExprType) =>
      t(`wallets.bankAccounts.status.${element.text}`),
    alpha2: (value: string) => t(`countries.${[constants.alpha2[value]]}`),
    alpha3: (value: string) => t(`countries.${[constants.alpha3[value]]}`),
    documentTypes: (element: DisplayExprType) => t(`wallets.bankAccounts.type.${element.text}`),
    documentStatuses: (element: DisplayExprType) =>
      t(`wallets.details.documents.status.${element.text}`),
    countries: (value: string) => t(`countries.${value}`),
    walletTypes: (element: DisplayExprType) => t(`wallets.types.${element.text}`),
    transactionWalletTypes: (element: DisplayExprType) =>
      t(`transactions.status.walletType.${element.text}`),
    walletUbos: (element: DisplayExprType) => t(`wallets.ubo.${element.text}`),
    walletStatuses: (element: DisplayExprType) => t(`wallets.status.${element.text}`),
    thresholdWalletStatuses: (element: DisplayExprType) =>
      t(`wallets.statusthresholds.${element.text}`),
    walletCategories: (element: DisplayExprType) => t(`wallets.category.${element.text}`),
    sddMandateStatuses: (element: DisplayExprType) =>
      t(`wallets.paymentMethods.sddMandates.status.${element.text}`),
    sddMandateLanguages: (element: DisplayExprType) => t(`common.languages.${element.text}`),
    ipAddressWhitelistApplications: (value: string) =>
      constants.ipAddressWhitelistApplications[value],
    clientTitles: (value: string) => t(`common.clientTitles.${constants.clientTitles[value]}`),
    savedCardStatuses: (element: DisplayExprType) =>
      t(`wallets.paymentMethods.savedCards.status.${element.text}`),
    virtualIbanCountries: (value: string) =>
      `${t(`countries.${constants.alpha2[value.toUpperCase()]}`)} (${constants.virtualIbanCountries[value].bank})`
  };
  const getLookupDataSource = (type: string, isAutocomplete = false, localeVersion?: number) => {
    let lookupDataSource = [];
    const textKey = isAutocomplete ? 'label' : 'text';
    const effectiveTextKey = localeVersion ? textKey : textKey;

    if (Array.isArray(constants[type])) {
      lookupDataSource = constants[type].map((element: DisplayExprType) => {
        return {
          ...element,
          [effectiveTextKey]: displayCallbacks[type](element)
        };
      });
    } else {
      lookupDataSource = Object.keys(constants[type]).map((value) => {
        return {
          ...constants[type][value],
          value,
          [effectiveTextKey]: displayCallbacks[type](value)
        };
      });
    }

    return lookupDataSource.sort((a: DisplayExprType, b: DisplayExprType) => a.textKey - b.textKey);
  };

  const getDisplayExpr = (type: string, value: number | string | undefined) => {
    const found = type
      ? getLookupDataSource(type).find((element: DisplayExprType) => element.value === value)
      : null;
    return found ? found.text : value;
  };

  const handleChips = (type: string, value: number | string, isDataGrid = false) => {
    const found = getLookupDataSource(type).find(
      (element: DisplayExprType) => element.value === value
    );

    const chipArgs = {
      value: found || { text: value, icon: 'Support' }
    };

    if (isDataGrid) {
      chipArgs.value.breakLine = true;
    }

    return chipArgs;
  };

  const getCountriesDataSource = () => {
    let lookupDataSource = [];

    lookupDataSource = Object.keys(constants.alpha3).map((value) => {
      return {
        value,
        label: displayCallbacks.countries(value)
      };
    });

    return lookupDataSource;
  };

  const getAlpha2Countries = () => {
    const countries = getLookupDataSource('alpha2');
    return countries
      .map((country: { value: string; label: string; text: string }) => {
        return { ...country, label: country.text };
      })
      .sort((a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label));
  };

  const getDisplayExprNationality = (text: string) => {
    if (text) {
      const nationalities = text.split(',');

      nationalities.forEach((nationality, index) => {
        nationalities[index] = getDisplayExpr('alpha3', nationality);
      });

      if (nationalities) {
        text = nationalities.join(', ');
      }
    }

    return text;
  };

  const findDisplayTextToValue = (type: string, displayText: string | number) => {
    const found = getLookupDataSource(type).filter(
      (element: DisplayExprType) => element.text.toLowerCase() === String(displayText).toLowerCase()
    );
    return found.length > 0 ? found.map((element: DisplayExprType) => element.value) : null;
  };

  const mapDisplayTextToValue = (type: string, displayText: string | number) => {
    const found = getLookupDataSource(type).find((element: DisplayExprType) =>
      element.text.toLowerCase().includes(String(displayText).toLowerCase())
    );
    return found ? found.value : null;
  };

  const downloadDocument = (src: string, name: string) => {
    const link = document.createElement('a');
    link.href = src;
    link.download = name;
    link.click();
  };

  const retryApiCall = async (
    apiCall: () => Promise<any>,
    retries: number = 5,
    delay: number = 1000
  ) => {
    let attempt = 0;
    while (attempt < retries) {
      try {
        const response = await apiCall();
        return response;
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : (error as { message?: string })?.message;
        if (errorMessage === 'Internal Server Error' && attempt < retries - 1) {
          attempt++;
          await new Promise((resolve) => setTimeout(resolve, delay));
        } else {
          throw error;
        }
      }
    }
  };

  const convertToSearchParams = (queryObject: Record<string, any>): URLSearchParams => {
    return new URLSearchParams(
      Object.entries(queryObject).map(([key, value]) => [key, String(value)])
    );
  };

  const createFilterExpressionCallback = (field: string, fieldType: string) => {
    return (filterValue: string | number, selectedFilterOperation: string) => {
      return createFilterExpressionCore(field, fieldType, filterValue, selectedFilterOperation);
    };
  };

  const createFilterExpressionCore = (
    field: string,
    fieldType: string,
    filterValue: string | number,
    selectedFilterOperation: string
  ) => {
    const values = findDisplayTextToValue(fieldType, filterValue);
    const filterExpression = [];
    if (values && values.length > 0) {
      values.forEach((value: string | number, index: number) => {
        filterExpression.push([field, selectedFilterOperation ?? '=', value]);
        if (index < values.length - 1) {
          filterExpression.push('or');
        }
      });
    } else {
      filterExpression.push([field, selectedFilterOperation ?? '=', filterValue]);
    }
    return filterExpression;
  };

  const createDateFilter = (dateRange: string[]): (string | string[])[] => {
    const dateFilter: (string | string[])[] = [];
    if (!dateRange && dateFilter.length === 0) {
      return dateFilter;
    }
    if (dateRange[0]) {
      dateFilter.push(['date', '>=', dateRange[0]]);
    }
    if (dateRange.length === 2) {
      dateFilter.push('and');
    }
    if (dateRange[1]) {
      dateFilter.push(['date', '<=', dateRange[1]]);
    }
    return dateFilter;
  };

  const getEnumOptions = <T extends Record<string, string>>(enumMap: T, translationKey: string) =>
    Object.entries(enumMap)
      .map(([key, value]) => ({
        value,
        text: t(`${translationKey}.${camelCase(key)}`)
      }))
      .sort((a, b) => a.text.localeCompare(b.text));

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.replace(/^data:.*;base64,/, '');
        resolve(base64);
      };

      reader.onerror = (error) => {
        reject(error);
      };

      reader.readAsDataURL(file);
    });
  };

  const deepEqual = <T>(a: T, b: T): boolean => {
    return JSON.stringify(a) === JSON.stringify(b);
  };

  return {
    getDisplayExpr,
    handleChips,
    getDisplayExprNationality,
    getCountriesDataSource,
    getAlpha2Countries,
    mapDisplayTextToValue,
    getLookupDataSource,
    downloadDocument,
    retryApiCall,
    convertToSearchParams,
    createFilterExpressionCallback,
    createFilterExpressionCore,
    createDateFilter,
    toPascalCaseName,
    fileToBase64,
    notProduction,
    getEnumOptions,
    deepEqual
  };
};
